<template>
  <div class="wrapper">
    <div class="header">Generate New Overview</div>
    <div class="po-gen-form">
      <div>
        <span>Range Start</span>
        <form-date-picker v-model="overviewParams.rangeStart" @change="onRangeStartChange" />
      </div>
      <div>
        <span>Range End</span>
        <form-date-picker v-model="overviewParams.rangeEnd" @change="onRangeEndChange" />
      </div>
      <div>
        <span>Pay Period Type</span>
        <DropdownSelect
          :menu-items="payPeriods"
          selected-item=""
          class="pay-period-dropdown blue"
          @select-item="onPayPeriodSelect"
        />
      </div>
      <div>
        <span>Departments</span>
        <tol-multi-select
          class="blue"
          :item-list="departments"
          @item-change="addRemoveDepartment($event)"
        />
      </div>
    </div>
    <span v-if="hasValidationErrors" class="validation-error">
      {{ $t('payrollOverviews.listing.errors.dateValidation') }}
    </span>
    <div v-if="isParamsSelected && !hasValidationErrors" class="action" @click="onGenerateClick()">
      {{ $t('generate') }}
    </div>
  </div>
</template>

<script>
import { isAfter, isFirstDayOfMonth, isLastDayOfMonth } from 'date-fns';
import FormDatePicker from '@/app/components/FormDatePicker';
import DropdownSelect from '@/app/components/DropdownSelect';
import TolMultiSelect from '@/app/components/TolMultiSelect';
import { AppGetters } from '@/app/app.keys';

import format from 'date-fns/format';

export default {
  name: 'PayrollOverviewGenerationForm',
  components: {
    FormDatePicker,
    DropdownSelect,
    TolMultiSelect
  },
  data() {
    return {
      payPeriods: ['Weekly', 'Bi Weekly', 'Semi Monthly', 'Monthly'],
      overviewParams: {
        rangeStart: null,
        rangeEnd: null,
        payPeriodType: null,
        departmentCodes: []
      }
    };
  },
  computed: {
    departments() {
      return this.$store.getters[AppGetters.Departments].map(item => item.code);
    },
    hasValidationErrors() {
      if (this.overviewParams.rangeStart) {
        if (isAfter(this.overviewParams.rangeStart, Date.now())) {
          return true;
        }
      }
      if (this.overviewParams.rangeStart && this.overviewParams.rangeEnd) {
        if (isAfter(this.overviewParams.rangeStart, this.overviewParams.rangeEnd)) {
          return true;
        }
      }
      if (
        this.overviewParams.rangeStart &&
        this.overviewParams.rangeEnd &&
        this.overviewParams.payPeriodType === 'Monthly'
      ) {
        // Check if the report start and end days are the first and last days of the month,
        // with the exception of department codes that allow for monthly reports with custom dates
        const customMonthDeptCodes = ['T@', 'T!', 'TV', 'A@,', 'A!', 'AV', 'B@', 'BV', 'B!', 'M!'];
        const hasCustomMonthDeptCodes =
          this.overviewParams.departmentCodes.length > 0 &&
          this.overviewParams.departmentCodes.every(code => customMonthDeptCodes.includes(code));
        if (!hasCustomMonthDeptCodes) {
          if (
            !isFirstDayOfMonth(this.overviewParams.rangeStart) ||
            !isLastDayOfMonth(this.overviewParams.rangeEnd)
          ) {
            return true;
          }
        }
      }
      return false;
    },
    isParamsSelected() {
      return (
        this.overviewParams.rangeStart &&
        this.overviewParams.rangeEnd &&
        this.overviewParams.payPeriodType &&
        this.overviewParams.departmentCodes.length
      );
    }
  },
  methods: {
    addRemoveDepartment(evt) {
      this.overviewParams.departmentCodes = evt;
    },
    onRangeEndChange(evt) {
      this.overviewParams.rangeEnd = evt;
    },
    onRangeStartChange(evt) {
      this.overviewParams.rangeStart = evt;
    },
    onPayPeriodSelect(evt) {
      this.overviewParams.payPeriodType = evt;
    },
    onGenerateClick() {
      this.$emit('generate', {
        rangeStart: format(this.overviewParams.rangeStart, 'YYYY-MM-DD'),
        rangeEnd: format(this.overviewParams.rangeEnd, 'YYYY-MM-DD'),
        payPeriodType: this.toUnderscoreAndUpperCase(this.overviewParams.payPeriodType),
        departmentCodes: this.overviewParams.departmentCodes
      });
    },
    toUnderscoreAndUpperCase(str) {
      if (str) {
        const parts = str.toUpperCase().split(' ');
        return parts.join('_');
      }
      return str;
    }
  }
};
</script>
<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.wrapper {
  display: flex;
  flex-direction: column;
  padding: 60px;
  background-color: $white;
  .header {
    font-weight: bold;
    font-size: 20px;
    margin-bottom: $vertical-margin-regular;
    border-bottom: 1px solid $row-border;
  }

  .po-gen-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: $vertical-margin-regular;

    @media only screen and (min-width: 768px) {
      flex-direction: row;
    }

    div {
      margin-right: 15px;
      width: 250px;
    }

    span {
      display: inline-block;
      padding-bottom: 12px;
      font-weight: bold;
    }

    .pay-period-dropdown {
      height: 40px;
    }
  }

  .validation-error {
    width: fit-content;
    color: $red;
  }

  .action {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 70px;
    width: 250px;
    background-color: $secondary-green;
    font-weight: bold;
    color: $white;
    &:hover {
      cursor: pointer;
      background-color: $green;
    }
  }
}
</style>
